import { keys } from "@/lib/i18n/portal";
import type {
  ProductListResponse,
  ProductSellerHiddenReason,
} from "@/lib/api/types";

/** Derived display status shared by the grid card, table row and mobile row. */
export type ProductCardStatus = "active" | "low_stock" | "out_of_stock" | "hidden";

/**
 * Fallback low-stock threshold when neither the product nor the store specifies
 * one. Matches the store-settings default (10).
 */
export const LOW_STOCK_THRESHOLD = 10;

type ProductItem = ProductListResponse["items"][number];
type Variant = ProductItem["variants"][number];

/** Minimal translate signature — matches what `useTranslations()` returns. */
type Translate = (
  key: string,
  values?: Record<string, string | number | null | undefined>,
) => string;

export function isPurchasableVariant(variant: Variant): boolean {
  return (
    variant.isActive &&
    !variant.forceOutOfStock &&
    (variant.unlimitedStock || variant.stockCount > 0)
  );
}

export function getPurchasableStockTotal(product: ProductItem): number {
  return product.variants
    .filter(isPurchasableVariant)
    .reduce((total, variant) => total + variant.stockCount, 0);
}

/**
 * True when the product sells with unlimited stock. Such a product always has a
 * purchasable-stock total of 0 (its variants carry no count), so callers must
 * check this before reading `stockTotal === 0` as "out of stock".
 */
export function hasUnlimitedStock(product: ProductItem): boolean {
  return product.variants
    .filter(isPurchasableVariant)
    .some((variant) => variant.unlimitedStock);
}

export function deriveProductStatus(
  product: ProductItem,
  storeLowStockThreshold?: number,
): ProductCardStatus {
  const hiddenReasons = product.sellerBuyerHiddenReasons ?? [];
  if (
    hiddenReasons.includes("product_inactive") ||
    hiddenReasons.includes("missing_images")
  ) {
    return "hidden";
  }
  const purchasable = product.variants.filter(isPurchasableVariant);
  if (purchasable.length === 0) return "out_of_stock";
  // Unlimited stock (effective, product-level) can never be low or run out.
  if (purchasable.some((variant) => variant.unlimitedStock)) return "active";
  // Effective threshold: the product's own override wins, else the store
  // default, else the module fallback.
  const threshold =
    product.lowStockThreshold ?? storeLowStockThreshold ?? LOW_STOCK_THRESHOLD;
  if (getPurchasableStockTotal(product) <= threshold) return "low_stock";
  return "active";
}

export type CardAttentionAction =
  | "navigate"
  | "toggleVisible"
  | "addPhoto"
  | "editStock";

export type CardAttention = {
  tone: "warning" | "error";
  message: string;
  ctaLabel: string;
  /**
   * `toggleVisible` → the fix is a one-tap visibility flip (the product is
   * complete, just switched off). `navigate` → the fix lives in the editor
   * (add a photo / option / restock).
   */
  action: CardAttentionAction;
};

type AttentionInput = {
  status: ProductCardStatus;
  hiddenReasons: ProductSellerHiddenReason[];
  /** Sellable stock — used for the low-stock count. */
  stockTotal: number;
};

/**
 * Diagnose what (if anything) is keeping a product from selling, and surface
 * the specific blocker + a route to its fix. Healthy products return `null`
 * so their card/row stays calm. Shared by all three product views so the
 * grid card, desktop table and mobile list never diverge.
 */
export function getProductCardAttention(
  { status, hiddenReasons, stockTotal }: AttentionInput,
  t: Translate,
): CardAttention | null {
  if (status === "hidden") {
    // A missing photo can't be fixed by flipping visibility — route to the editor.
    if (hiddenReasons.includes("missing_images")) {
      return {
        tone: "warning",
        message: t(keys.products.attnNeedsPhotoMsg),
        ctaLabel: t(keys.products.attnAddPhoto),
        action: "addPhoto",
      };
    }
    return {
      tone: "warning",
      message: t(keys.products.attnHiddenMsg),
      ctaLabel: t(keys.products.attnMakeVisible),
      action: "toggleVisible",
    };
  }

  if (status === "out_of_stock") {
    if (hiddenReasons.includes("no_active_purchasable_variants")) {
      return {
        tone: "error",
        message: t(keys.products.attnNeedsVariantMsg),
        ctaLabel: t(keys.products.attnAddVariant),
        action: "navigate",
      };
    }
    return {
      tone: "error",
      message: t(keys.products.attnOutOfStockMsg),
      ctaLabel: t(keys.products.attnRestock),
      action: "editStock",
    };
  }

  if (status === "low_stock") {
    return {
      tone: "warning",
      message: t(keys.products.attnLowStockMsg, { count: stockTotal }),
      ctaLabel: t(keys.products.attnRestock),
      action: "editStock",
    };
  }

  return null;
}
